/**
 * confirmAction - Promise-based branded confirm dialog
 * ======================================================
 *
 * One-liner replacement for `window.confirm()` that renders the same
 * branded UX as `<ConfirmDestructive>` but is usable from inside async
 * handlers without restructuring the calling component.
 *
 * Usage:
 *   import { confirmAction } from '../utils/confirmAction';
 *
 *   if (!(await confirmAction({
 *     name: 'Invoice INV-001',
 *     impact: 'This will reverse the write-off and restore the balance.',
 *     destructiveLabel: 'Reverse write-off',
 *   }))) return;
 *
 *   // proceed with destructive action
 *
 * The helper:
 *   • Mounts the dialog in a fresh React root attached to <body>
 *   • Resolves the returned promise with true (confirm) / false (cancel/close)
 *   • Auto-unmounts after the user acts so multiple confirms work
 *
 * IMPORTANT: This is a pragmatic shim for legacy `window.confirm` call
 * sites. New flows should still use the JSX `<ConfirmDestructive>`
 * component for the richer UI (typed-confirm, archive-instead, etc.).
 */
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ConfirmDestructive } from '../components/common/ConfirmDestructive';

const ConfirmShell = ({ options, resolve }) => {
  const [open, setOpen] = useState(true);
  return (
    <ConfirmDestructive
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setOpen(false);
          // Default to cancel when the dialog is dismissed (X / overlay).
          resolve(false);
        }
      }}
      name={options.name}
      impact={options.impact}
      destructiveLabel={options.destructiveLabel || 'Confirm'}
      confirmingLabel={options.confirmingLabel || 'Working…'}
      cancelLabel={options.cancelLabel || 'Cancel'}
      requireType={options.requireType || false}
      onConfirm={() => {
        setOpen(false);
        resolve(true);
      }}
      testIdPrefix={options.testIdPrefix || 'confirm-action'}
    />
  );
};

export function confirmAction(options) {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    host.setAttribute('data-confirm-action-host', '');
    document.body.appendChild(host);
    const root = createRoot(host);
    const cleanup = () => {
      // Defer unmount so React finishes its current commit phase.
      setTimeout(() => {
        try { root.unmount(); } catch (_e) {}
        try { host.remove(); } catch (_e) {}
      }, 200);
    };
    const wrappedResolve = (value) => {
      resolve(!!value);
      cleanup();
    };
    root.render(<ConfirmShell options={options} resolve={wrappedResolve} />);
  });
}

export default confirmAction;
